import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AuthSpinner } from "@/components/AuthSpinner";
import { cachedOnboardingComplete, fetchOnboardingComplete } from "@/lib/clientOnboarding";

/**
 * Keeps a brand-new athlete in the intake questionnaire until it is done.
 * Coaches and admins are never gated, and every existing athlete was
 * backfilled as complete, so nobody already using the app is interrupted.
 */
export function RequireOnboarding({ children }: { children: JSX.Element }) {
  const { user, role } = useAuth();
  const location = useLocation();
  const userId = user?.id ?? null;
  const [state, setState] = useState<"checking" | "done" | "needed">(() =>
    userId && cachedOnboardingComplete(userId) ? "done" : "checking",
  );

  useEffect(() => {
    if (!userId || role !== "client") return;
    if (cachedOnboardingComplete(userId)) {
      setState("done");
      return;
    }
    let active = true;
    setState("checking");
    void fetchOnboardingComplete(userId).then((done) => {
      if (active) setState(done ? "done" : "needed");
    });
    return () => {
      active = false;
    };
  }, [userId, role]);

  if (!userId || role !== "client") return children;
  if (state === "checking") return <AuthSpinner label="Loading…" />;
  if (state === "needed") {
    return (
      <Navigate
        to="/welcome"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  return children;
}
